import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getReturnLabel, getReturnPath } from "../../../utils/navReturn";
import useAuth from "../../../hooks/useAuth";
import { canReviewConfirmations } from "../../../utils/permissions";
import confirmationService from "../../../services/confirmation.service";
import ConfirmationExcelList from "./ConfirmationExcelList";
import { sortConfirmationsNewestFirst } from "./confirmationListUtils";
import { formatVehicleRequestCount } from "./groupPendingConfirmations";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import "../../../styles/users.css";
import "../../../styles/confirmation.css";

export default function ConfirmationVehicleRequests() {
  const { vehicleKey: rawKey } = useParams();
  const vehicleKey = decodeURIComponent(rawKey || "");
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRepoAdmin = canReviewConfirmations(auth);
  const returnTo = getReturnPath(
    new URLSearchParams(),
    location.state,
    "/confirmation?status=PENDING"
  );
  const returnLabel = getReturnLabel(returnTo);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const loadSeqRef = useRef(0);

  const loadRequests = useCallback(async () => {
    if (!auth?.token || !vehicleKey) {
      setLoading(false);
      return;
    }
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      setError("");
      const res = await confirmationService.getAll(auth.token, {
        vehicleKey,
        page,
        limit: PAGE_LIMIT,
      });
      if (seq !== loadSeqRef.current) return;
      setItems(readListItems(res));
      setPagination(readPagination(res));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(err?.response?.data?.message || "Failed to load vehicle requests");
      setItems([]);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [auth?.token, vehicleKey, page]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const displayed = useMemo(
    () => sortConfirmationsNewestFirst(items, "PENDING", ""),
    [items]
  );

  const plateLabel = formatVehicleNumberDisplay(
    location.state?.vehicleNumber || displayed[0]?.vehicleNumber || ""
  );

  const openConfirmation = (item) => {
    if (!item?._id) return;
    const listReturn = `${location.pathname}${location.search}`;
    if (isRepoAdmin) {
      navigate(`/confirmation/${item._id}`, { state: { from: listReturn } });
      return;
    }
    if (String(item.status || "").toUpperCase() === "CONFIRMED") {
      navigate(`/inventory-update?confirmationId=${item._id}`);
    }
  };

  const openInventory = (item) => {
    if (!item?._id) return;
    navigate(`/inventory-update?confirmationId=${item._id}`);
  };

  return (
    <div className="page cf-page cf-page--list-only">
      <div className="cf-view-top">
        <Link to={returnTo} className="cf-view-back">
          ← Back to {returnLabel}
        </Link>
      </div>

      <header className="cf-page__title-row">
        <h2 className="cf-page__title">
          {plateLabel !== "—" ? plateLabel : "Vehicle requests"}
        </h2>
        {!loading && displayed.length > 0 ? (
          <p className="cf-list-count" aria-live="polite">
            {formatVehicleRequestCount(pagination.total || displayed.length)}
          </p>
        ) : null}
      </header>

      {error ? <p className="error-text">{error}</p> : null}

      <section className="cf-table-card card cf-table-card--responsive">
        {loading ? (
          <p className="cf-excel-loading">Loading…</p>
        ) : displayed.length === 0 ? (
          <p className="cf-excel-loading cf-list-empty">
            No confirmation requests found for this vehicle.
          </p>
        ) : (
          <ConfirmationExcelList
            items={displayed}
            isRepoAdmin={isRepoAdmin}
            listFilter=""
            forceViewAction
            startSerial={Math.max(
              Number(pagination.total || displayed.length) -
                ((pagination.page || page) - 1) * PAGE_LIMIT,
              displayed.length
            )}
            onView={openConfirmation}
            onInventory={openInventory}
          />
        )}
      </section>
      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
